import { useState } from 'react';
import { Clock } from 'lucide-react';
import { theme } from '../../../theme';
import { formatDateTime } from '../hooks/useRelativeTime';
import { updateCaseStatus } from '../services/assessmentHistoryService';
import { CASE_STATUSES, CASE_STATUS_LABELS } from '../types/assessmentHistory.types';
import type { CaseStatus, StatusHistoryEntry } from '../types/assessmentHistory.types';

type StatusTrackerProps = {
  incidentId: string;
  currentStatus: CaseStatus;
  statusHistory: StatusHistoryEntry[];
  currentUserId: string;
  canEditStatus: boolean;
  onStatusChanged: (newStatus: CaseStatus, entry: StatusHistoryEntry) => void;
};

export const StatusTracker = ({
  incidentId,
  currentStatus,
  statusHistory,
  currentUserId,
  canEditStatus,
  onStatusChanged,
}: StatusTrackerProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const handleStatusChange = async (newStatus: CaseStatus) => {
    if (newStatus === currentStatus || isUpdating) return;

    setIsUpdating(true);
    setUpdateError(null);
    try {
      const entry = await updateCaseStatus(incidentId, newStatus, currentUserId);
      onStatusChanged(newStatus, entry);
    } catch (err) {
      console.error('Failed to update status:', err);
      setUpdateError('Could not save status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      {/* Current status selector */}
      {canEditStatus && (
        <div className="mb-4">
          <select
            value={currentStatus}
            onChange={(e) => handleStatusChange(e.target.value as CaseStatus)}
            disabled={isUpdating}
            className={`${theme.input.select} ${isUpdating ? 'opacity-50' : ''}`}
          >
            {CASE_STATUSES.map((s) => (
              <option key={s} value={s}>{CASE_STATUS_LABELS[s]}</option>
            ))}
          </select>
          {updateError && (
            <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">{updateError}</p>
          )}
        </div>
      )}

      {/* Status history timeline */}
      <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${theme.border.base}`}>
        <Clock className={`w-3.5 h-3.5 ${theme.text.muted}`} />
        <h4 className={`text-xs font-bold uppercase tracking-widest ${theme.text.muted}`}>
          Status History
        </h4>
      </div>

      {statusHistory.length === 0 ? (
        <p className={`text-xs ${theme.text.muted}`}>No status changes recorded.</p>
      ) : (
        <div className="relative pl-4">
          {/* Vertical timeline line */}
          <div className={`absolute left-[7px] top-1 bottom-1 w-px ${theme.border.base} border-l`} />

          <div className="space-y-3">
            {statusHistory.map((entry) => (
              <div key={entry.id} className="relative flex items-start gap-3">
                {/* Timeline dot */}
                <div className="absolute -left-4 top-1 w-2.5 h-2.5 rounded-full dark:bg-sy-teal bg-teal-500 border-2 dark:border-sy-navy border-white z-10" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full dark:bg-white/[0.06] bg-gray-100 dark:text-white/70 text-gray-700">
                      {CASE_STATUS_LABELS[entry.status]}
                    </span>
                    <span className={`text-[10px] ${theme.text.muted}`}>
                      {formatDateTime(entry.changed_at)}
                    </span>
                  </div>
                  {entry.note && (
                    <p className={`text-xs ${theme.text.body} mt-0.5`}>{entry.note}</p>
                  )}
                  <p className={`text-[10px] ${theme.text.muted} mt-0.5`}>
                    {entry.changed_by_name ?? 'System'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
